// DiseaseDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  getDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../Environment/firebase";
import { CaseCardType } from "../../Modules/ViewCase/CaseCard";
import { Typography, Grid, Container, Box, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useWindowDimensions } from "../../Functions/ScreenOperation/useWindowDimension";
import CaseCard from "../../Modules/ViewCase/CaseCard";
import { useNavigate } from "react-router-dom";
import DiseaseData from "./DiseaseData";

type Disease = {
  id: string;
  name: string;
  tag: string;
  definition: string;
  common_age: string;
  symptom: string;
  case_list: string[];
};

const DiseasePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [disease, setDisease] = useState<Disease | null>(null);
  const [relatedCases, setRelatedCases] = useState<CaseCardType[]>([]);
  const [cases, setCases] = useState<CaseCardType[]>([]);
  const [error, setError] = useState<string>("");

  const { width } = useWindowDimensions();

  useEffect(() => {
    const fetchDisease = async () => {
      try {
        const docRef = doc(collection(db, "search"), id!);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const fetchedDisease: Disease = {
            id: docSnap.id,
            name: data.name,
            tag: data.tag,
            definition: data.definition,
            common_age: data.common_age || "情報なし",
            symptom: data.symptom || "情報なし",
            case_list: data.case_list || [],
          };
          setDisease(fetchedDisease);

          if (fetchedDisease.case_list && fetchedDisease.case_list.length > 0) {
            // 症例情報を取得
            const caseQuery = query(
              collection(db, "dwh"),
              where("share", "==", "true"),
              where("id", "in", fetchedDisease.case_list)
            );
            const caseSnap = await getDocs(caseQuery);

            const fetchedCases: CaseCardType[] = caseSnap.docs.map((doc) => {
              const caseData = doc.data();
              return {
                id: caseData.id || "",
                title: caseData.title || "",
                age: caseData.ageSearch || "",
                sex: caseData.sex || "",
                patientId: caseData.patientId || "",
                site: caseData.site || "",
                modality: caseData.modality || "",
                date: caseData.uploadDate || "",
                organTags: caseData.organTags || [],
                keyImages: caseData.KeyImages || [],
                casenumber: caseData.casenumber || "",
              };
            });

            setCases(fetchedCases);

            const allDocsSnap = await getDocs(
              query(collection(db, "dwh"), where("share", "==", "true"))
            );
            const filteredCases: CaseCardType[] = allDocsSnap.docs
              .filter((doc) => {
                const caseData = doc.data();
                return (
                  (caseData.title &&
                    caseData.title.includes(fetchedDisease.name)) ||
                  (caseData.findings &&
                    caseData.findings.includes(fetchedDisease.name)) ||
                  (caseData.diagnosis &&
                    caseData.diagnosis.includes(fetchedDisease.name))
                );
              })
              .map((doc) => {
                const caseData = doc.data();
                return {
                  id: caseData.id || "",
                  title: caseData.title || "",
                  age: caseData.ageSearch || "",
                  sex: caseData.sex || "",
                  patientId: caseData.patientId || "",
                  site: caseData.site || "",
                  modality: caseData.modality || "",
                  date: caseData.uploadDate || "",
                  organTags: caseData.organTags || [],
                  keyImages: caseData.KeyImages || [],
                  casenumber: caseData.casenumber || "",
                };
              });

            setRelatedCases(filteredCases);
          }
        } else {
          setError("病気の情報が見つかりませんでした。");
        }
      } catch (err) {
        console.error("エラーが発生しました:", err);
        setError("病気の情報を取得中にエラーが発生しました。");
      }
    };

    fetchDisease();
  }, [id]);

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // -1は前のページに戻ることを意味します
  };

  return (
    <>
      {width >= 1300 && (
        <Box
          sx={{
            position: "absolute",
            left: "calc((100vw - 1200px) / 2 - 36px)",
            top: "96px",
          }}
        >
          <IconButton
            sx={{
              color: "#9CA4AD",
              border: "1px solid #9CA4AD",
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      )}
      <Container disableGutters>
        {error && <p>{error}</p>}
        {disease && <DiseaseData disease={disease} cases={cases} />}
        {relatedCases.length > 0 && (
          <Container sx={{ mt: 4, mb: 4 }}>
            <Container
              sx={{
                mt: 4,
                mb: 4,
              }}
            >
              {relatedCases.some((relatedCase) => !cases.find((c) => c.id === relatedCase.id)) && (
              <Typography
                variant="h6"
                style={{ marginTop: "20px" }}
                textAlign={"left"}
                sx={{ textAlign: "left", mb: 1 }}
              >
                その他の症例(
                {
                  relatedCases.filter(
                    (relatedCase) => !cases.find((c) => c.id === relatedCase.id)
                  ).length
                }
                )
              </Typography>
              )}
              <Grid
                container
                spacing={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "start",
                  gridTemplateColumns: { sm: "50% 50%", xs: "100%" },
                }}
              >
                {relatedCases.filter(
                  (relatedCase) => !cases.find((c) => c.id === relatedCase.id)
                ).length > 0 &&
                  relatedCases
                    .filter(
                      (relatedCase) =>
                        !cases.find((c) => c.id === relatedCase.id)
                    )
                    .map((CaseData: CaseCardType) => (
                      <Grid item key={CaseData.id} xs={12} sm={6}>
                        <CaseCard
                          id={CaseData.id}
                          title={CaseData.title}
                          age={CaseData.age}
                          sex={CaseData.sex}
                          patientId={CaseData.patientId}
                          site={CaseData.site}
                          modality={CaseData.modality}
                          date={CaseData.date}
                          organTags={CaseData.organTags}
                          keyImages={CaseData.keyImages}
                          casenumber={CaseData.casenumber}
                        />
                      </Grid>
                    ))}
              </Grid>
            </Container>
          </Container>
        )}
      </Container>
    </>
  );
};

export default DiseasePage;
